/**
 * @file porousCalculation.js
 * @author Minh Phuong, James Feracor, Charles W. Schwartz, Ben Ciavola
 * @copyright © Pavia Systems, Trisight LLC 2019. All rights reserved.
 * Algorithm to determine Structural Number, Composite Subgrade Modulus and Asphalt Surface Thickness for Porous Pavement type
 *
 * Updated by Trisight LLC. 2019. Converted to typescript implementation.
 */

/** `@function`
 * @name calculatePorous
 * Return an object that contains Structural Number, Composite Subgrade Modulus and Asphalt Surface Thickness for Porous Pavement type
 * @param {float} asphaltCoefficient - usually use 0.40 for porous type
 * @param {float} appliedLoad_psi - applied load on porous pavement type, unit psi (q)
 * @param {float} loadDiameter_in - load diameter of load, unit inch (a)
 * @param {float} deltaPsi - allowable change in the Present Serviceability Index (PSI) over design life (ΔPSI)
 * @param {float} stdNormalDeviate -  standard normal deviate/level of design reliability (zR)
 * @param {float} standardDeviation - standard deviation (s0)
 * @param {float} esal - number of 18-kip equivalent single axle loads (ESALs) expected over design life (w18)
 * @param {float} layer1Modulus - Modulus of Stone Recharge Bed (E1)
 * @param {float} layer2Modulus - Modulus of Uncompacted Subgrade (E2)
 * @param {float} layer1Thickness_in - Stone Recharge Bed thickness (h1)
 */

export const calculatePorous = function (asphaltCoefficient: number,
                                         appliedLoad_psi: number,
                                         loadDiameter_in: number,
                                         deltaPsi: number,
                                         stdNormalDeviate: number,
                                         standardDeviation: number,
                                         esal: number,
                                         layer1Modulus: number,
                                         layer2Modulus: number,
                                         layer1Thickness_in: number) {
  // Declare variables for scoping purposes.
  let f2 = 0;

  // console.log(    asphaltCoefficient);
  // console.log(    appliedLoad_psi);
  // console.log(    loadDiameter_in);
  // console.log(    deltaPsi);
  // console.log(    stdNormalDeviate);
  // console.log(    standardDeviation);
  // console.log(    esal);
  // console.log(    layer1Modulus);
  // console.log(    layer2Modulus);
  // console.log(    layer1Thickness_in);


  /** @function
   * @name calculateSubgradeModulus
   * Return Composite Subgrade Modulus (mR)
   * @param {float} appliedLoad - applied load on porous pavement type, unit psi (q)
   * @param {float} loadDiameter - load diameter of load, unit inch (a)
   * @param {float} layer1Modulus - Modulus of Stone Recharge Bed (E1)
   * @param {float} layer2Modulus - Modulus of Uncompacted Subgrade (E2)
   * @param {float} layer1Thickness - Stone Recharge Bed thickness (h1)
   */
  const calculateSubgradeModulus = function (appliedLoad: number,
                                             loadDiameter: number,
                                             layer1Modulus: number,
                                             layer2Modulus: number,
                                             layer1Thickness: number) {
    // Declare Variables
    const c = 0;
    const k = 0;
    /** @function
     * @name calculateF2
     * Return Burmister's 2-layer deflection factor (F2)
     * @param {float} c - multiplication ratio between surface deflection and the exponential of Stone Recharge Bed thickness:Load Diameter ratio
     * @param {float} k - exponential of Stone Recharge Bed thickness:Load Diameter ratio
     * @param {float} loadDiameter - load diameter of load, unit inch (a)
     * @param {float} layer1Thickness - Stone Recharge Bed thickness (h1)
     */
    const calculateF2 = function (c: number, k: number, layer1Thickness: number, loadDiameter: number) {
      return c * (Math.pow((layer1Thickness / loadDiameter), k));
    };

    //Separate into different logic for perfect match E1/E2 and in-between E1/E2 regression curves. Hard coded ratio and exponential
    /**
     * If the match is perfect, use value of c and k from James Feracor's spreadsheet on Burmister Analysis to determine value of F2
     * If the match is not perfect, use value of c and k from James Feracor's spreadsheet on Burmister Analysis to calculate upper bound F2 and lower bound F2, then aggregate the result
     */

    if (layer1Modulus / layer2Modulus == 5000) {
      f2 = calculateF2(0.062944, -0.96004, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 2000) {
      f2 = calculateF2(0.085922, -0.94332, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 1000) {
      f2 = calculateF2(0.109008, -0.92007, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 500) {
      f2 = calculateF2(0.136121, -0.90273, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 200) {
      f2 = calculateF2(0.178131, -0.86336, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 100) {
      f2 = calculateF2(0.22016, -0.80628, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 50) {
      f2 = calculateF2(0.277553, -0.73309, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 20) {
      f2 = calculateF2(0.36976, -0.64697, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 10) {
      f2 = calculateF2(0.458323, -0.51515, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 5) {
      f2 = calculateF2(0.571671, -0.3855, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 2) {
      f2 = calculateF2(0.78399, -0.17575, layer1Thickness, loadDiameter);
    } else if (layer1Modulus / layer2Modulus >= 1) {
      f2 = calculateF2(1, 0, layer1Thickness, loadDiameter);
    }


    //* Return surface deflection (w0), unit inch
    const surfaceDeflection = 1.5 * appliedLoad * loadDiameter * f2 / layer2Modulus;

    //* Return Composite Subgrade Modulus of the 2 layers
    return 1.5 * appliedLoad * loadDiameter / surfaceDeflection;
  };

  //* Calculate the value of Composite Subgrade Modulus from user input
  const compositeSubgradeModulus = calculateSubgradeModulus(appliedLoad_psi, loadDiameter_in, layer1Modulus, layer2Modulus, layer1Thickness_in);

  console.log('CompositeSubgradeModulus:', compositeSubgradeModulus);

  /** @function
   * @name calculationAASHTO
   * Equation of Empirical AASHTO Flexible Pavement Design Equation (1993), used to solve Structural Design Number
   * @param {float} structuralNum - value of hypothesized structural number (SN)
   * @const {float} deltaPsi - allowable change in the Present Serviceability Index (PSI) over design life (ΔPSI)
   * @const {float} stdNormalDeviate -  standard normal deviate/level of design reliability (zR)
   * @const {float} standardDeviation - standard deviation (s0)
   * @const {float} esal - number of 18-kip equivalent single axle loads (ESALs) expected over design life (w18)
   * @const {float} asphaltCoefficient - usually use 0.40 for porous type
   * @const {float} compositeSubgradeModulus - calculated from previous step
   */
  const calculationAASHTO = function (structuralNum: number) {
    return (stdNormalDeviate * standardDeviation) + 9.36 * (Math.log10(structuralNum + 1)) - 0.2 + Math.log10(deltaPsi / 2.7) / (asphaltCoefficient + (1094 / Math.pow((structuralNum + 1), 5.19))) + 2.32 * Math.log10(compositeSubgradeModulus) - 8.07 - Math.log10(esal);
  };

  /** @function
   * @name secantSolution
   * Secant method for root finding,

   * @param {function} func - Function you would like to find the root for
   * @param {float} guess1 - Guess for the value
   * @param {float} guess2 - A different guess
   * @param {float} maxIterations - Maximum number of iterations for the solver to try
   * @param {float} accuracy - Accuracy desired
   */
  const secantSolution = function (func: (x: number) => number,
                                   guess1: number,
                                   guess2: number,
                                   maxIterations: number,
                                   accuracy: number) {
    let x0 = guess1;
    let x1 = guess2;
    let x2;

    for (let i = 1; i <= maxIterations; i++) {
      x2 = (x0 * func(x1) - x1 * func(x0)) / (func(x1) - func(x0));
      x0 = x1;
      x1 = x2;
      if (Math.abs(x1 - x0) < accuracy) {
        break
      }
    }
    return x1
  };

  const structuralNumFound = secantSolution(calculationAASHTO, 1, 4, 1000, 0.00001);

  // Return thickness of asphalt surface layer after calculation
  const thickness = structuralNumFound / asphaltCoefficient;

  // Return final object containing the calculated values
  const results = {
    f2: f2,
    structuralNum: structuralNumFound.toFixed(2),
    Mc: compositeSubgradeModulus.toFixed(2),
    asphaltThickness: thickness.toFixed(2)
  };
  return results;
};

